import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuthContext } from '../../Auth';
import { useCustomer } from '../../contexts/CustomerContext';

interface LineEntry {
  type: 'stdout' | 'stderr';
  text: string;
}

interface PipelineOutputProps {
  endpoint: string;
  body?: object;
  running: boolean;
  onStart: () => void;
  // Callback fires when the SSE stream signals 'exit' (or an unrecoverable
  // error). The second arg is the raw exit-event payload, so callers can
  // pull structured fields like tenant/customer_name from onboarding.
  onComplete: (code: number, exitPayload?: Record<string, unknown>) => void;
}

const PipelineOutput: React.FC<PipelineOutputProps> = ({
  endpoint,
  body,
  running,
  onStart,
  onComplete,
}) => {
  const { token } = useAuthContext();
  const { currentCustomer } = useCustomer();
  const [lines, setLines] = useState<LineEntry[]>([]);
  const [exitCode, setExitCode] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const prevRunningRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [lines, scrollToBottom]);

  useEffect(() => {
    // Only trigger when running transitions from false to true
    if (running && !prevRunningRef.current) {
      prevRunningRef.current = true;
      setLines([]);
      setExitCode(null);
      onStart();

      const controller = new AbortController();
      abortRef.current = controller;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      if (currentCustomer?.tenant && currentCustomer?.environment) {
        headers['X-Tenant'] = currentCustomer.tenant;
        headers['X-Environment'] = currentCustomer.environment;
      }

      fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body ?? {}),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            setLines((prev) => [
              ...prev,
              { type: 'stderr', text: `HTTP error: ${response.status} ${response.statusText}` },
            ]);
            onComplete(-1);
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            setLines((prev) => [...prev, { type: 'stderr', text: 'No response stream available' }]);
            onComplete(-1);
            return;
          }

          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
              const line = part.trim();
              if (!line.startsWith('data: ')) continue;

              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'stdout') {
                  setLines((prev) => [...prev, { type: 'stdout', text: data.text ?? '' }]);
                } else if (data.type === 'stderr') {
                  setLines((prev) => [...prev, { type: 'stderr', text: data.text ?? '' }]);
                } else if (data.type === 'stage-start') {
                  setLines((prev) => [
                    ...prev,
                    { type: 'stdout', text: `\n━━━ ${(data.stage ?? '').toUpperCase()} ━━━\n${data.command ? `$ ${data.command}\n` : ''}` },
                  ]);
                } else if (data.type === 'stage-end') {
                  setLines((prev) => [
                    ...prev,
                    { type: 'stdout', text: `━━━ ${(data.stage ?? '').toUpperCase()} exited ${data.code ?? '?'} ━━━\n` },
                  ]);
                } else if (data.type === 'exit') {
                  const code = data.code ?? 0;
                  if (data.message) {
                    setLines((prev) => [...prev, { type: code === 0 ? 'stdout' : 'stderr', text: `\n${data.message}\n` }]);
                  }
                  setExitCode(code);
                  onComplete(code, data);
                } else if (data.type === 'error') {
                  setLines((prev) => [...prev, { type: 'stderr', text: data.message ?? data.text ?? 'Unknown error' }]);
                  setExitCode(-1);
                  onComplete(-1);
                }
              } catch {
                // Ignore malformed SSE lines
              }
            }
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            setLines((prev) => [...prev, { type: 'stderr', text: `Connection error: ${err.message}` }]);
            setExitCode(-1);
            onComplete(-1);
          }
        });
    }

    if (!running) {
      prevRunningRef.current = false;
    }

    return () => {
      if (!running && abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [running, endpoint, body, token, currentCustomer, onStart, onComplete]);

  const isEmpty = lines.length === 0 && exitCode === null && !running;

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: '#1e1e1e',
        borderRadius: '8px',
        padding: '16px',
        minHeight: 300,
        maxHeight: 500,
        overflowY: 'auto',
        fontFamily: '"Fira Code", "Consolas", monospace',
        fontSize: 12,
      }}
    >
      {running && (
        <CircularProgress
          size={20}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#90caf9',
          }}
        />
      )}

      {isEmpty && (
        <Typography
          sx={{
            color: '#888',
            fontStyle: 'italic',
            fontFamily: 'inherit',
            fontSize: 'inherit',
          }}
        >
          Output will appear here...
        </Typography>
      )}

      {lines.map((line, idx) => (
        <Box
          key={idx}
          component="pre"
          sx={{
            margin: 0,
            padding: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: line.type === 'stderr' ? '#f44336' : '#d4d4d4',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 1.6,
          }}
        >
          {line.text}
        </Box>
      ))}

      {exitCode !== null && (
        <Typography
          sx={{
            mt: 2,
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'bold',
            color: exitCode === 0 ? '#4caf50' : '#f44336',
          }}
        >
          {exitCode === 0
            ? `Process completed successfully (exit code: 0)`
            : `Process failed (exit code: ${exitCode})`}
        </Typography>
      )}

      <div ref={bottomRef} />
    </Box>
  );
};

export default PipelineOutput;
